import React from 'react';
import { useParams } from 'react-router-dom';

import { useSearchParams } from 'Hooks/useSearchParams';
import alertToast from 'Util/ToastifyUtils';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { useUserDetail } from '../Hooks/useUserDetail';
import UserDetailInner from './UserDetailInner';

/**
 * Fetch wrapper for the staff user detail page. The Inner is keyed on dataUpdatedAt — the
 * remount-on-refetch is load-bearing: it rebuilds the edit session (form defaults + pricing
 * copy) from fresh server data after every save/cancel. The legacy useAsync wrapper got this
 * for free by unmounting through its loading state; useQuery keeps serving the previous user
 * during a refetch, so the key is what preserves the reset. Honours ?mode=edit, which legacy
 * staff bookmarks carry through the /staff-account-details redirect.
 */
export default function UserDetailPage() {
	const { id, tab } = useParams<{ id: string; tab: string }>();
	const { getSearchParam } = useSearchParams();

	const { data: user, isPending, isError, refetch, dataUpdatedAt } = useUserDetail(id);

	if (isError) {
		alertToast('Could not fetch user.', 'error');
		return <LottieSpinner />;
	}
	if (isPending) {
		return <LottieSpinner />;
	}
	if (isNullOrUndefined(user)) {
		alertToast('Could not fetch user', 'error');
		return <LottieSpinner />;
	}

	const defaultMode = getSearchParam('mode') === 'edit' ? EntityFormMode.EDIT : undefined;

	return (
		<UserDetailInner
			key={`${user.id}-${dataUpdatedAt}`}
			user={user}
			refetchUser={refetch}
			initialTab={tab ?? 'account'}
			defaultMode={defaultMode}
		/>
	);
}
